class QuickView {
  constructor() {
    this.init();
  }

  #closeQuickViewDock() {
    const dock = document.getElementById('QuickViewDock');
    if (!dock) return;

    if (window.dockManager) {
      window.dockManager.closeAll();
    } else {
      dock.classList.remove('active');
      document.body.classList.remove('dock-open');
    }
  }

  init() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.js-quick-view-trigger');
      if (!trigger) return;

      e.preventDefault();
      const productUrl = trigger.dataset.productUrl;
      if (productUrl) {
        this.open(productUrl, trigger);
      }
    });

    // Close Dock on successful Add-to-Cart from within Quick View
    document.addEventListener('product-form:submit:success', (e) => {
      const dock = document.getElementById('QuickViewDock');

      // Only proceed if the Quick View Dock exists and is currently active
      if (dock && dock.classList.contains('active')) {
        // Check if the form submitting is strictly inside the Quick View dock
        const isInsideDock = dock.contains(e.target);

        // Only close the Quick View dock if the submission originated from it
        if (isInsideDock) {
          this.#closeQuickViewDock();
        }
      }
    });
  }

  #normalizeLocaleRootPath(rootRaw) {
    if (rootRaw == null || rootRaw === '' || rootRaw === '/') return '';

    let s = String(rootRaw).trim();

    if (s.includes('://')) {
      try {
        s = new URL(s).pathname;
      } catch {
        return '';
      }
    }

    if (!s.startsWith('/')) {
      s = `/${s}`;
    }

    s = s.replace(/\/$/, '');

    return s || '';
  }

  #isPdpUpsellQuickViewTrigger(trigger) {
    if (!trigger) return false;

    if (trigger.dataset.pdpUpsellQuickView === 'true') {
      return true;
    }

    return Boolean(trigger.closest('[data-product-upsell-list]'));
  }

  #isBundleQuickViewTrigger(trigger) {
    return Boolean(trigger && trigger.dataset.bundleQuickView === 'true');
  }

  #isBundleSizeGuideTrigger(trigger) {
    return Boolean(trigger && trigger.dataset.bundleSizeGuide === 'true');
  }

  #sectionIdForTrigger(trigger) {
    if (this.#isBundleSizeGuideTrigger(trigger)) {
      return 'quick-view-bundle-size-guide';
    }

    if (this.#isBundleQuickViewTrigger(trigger)) {
      return 'quick-view-bundle';
    }

    return this.#isPdpUpsellQuickViewTrigger(trigger) ? 'quick-view-pdp-upsell' : 'quick-view';
  }

  #variantIdFromTrigger(trigger) {
    return trigger?.dataset?.variantId?.trim() ?? '';
  }

  #appendVariantParam(url, trigger) {
    const variantId = this.#variantIdFromTrigger(trigger);

    if (variantId) {
      url.searchParams.set('variant', variantId);
    }
  }

  #productUrlForSectionRequest(productUrl, sectionId, trigger) {
    const parsed = new URL(productUrl, window.location.origin);
    const rootNorm = this.#normalizeLocaleRootPath(window.routes?.root_url);
    let path = parsed.pathname;

    if (rootNorm) {
      const alreadyPrefixed = path === rootNorm || path.startsWith(`${rootNorm}/`);
      if (!alreadyPrefixed) {
        path = `${rootNorm}${path}`;
      }
    }

    const next = new URL(path, window.location.origin);

    for (const [key, value] of parsed.searchParams.entries()) {
      next.searchParams.append(key, value);
    }

    this.#appendVariantParam(next, trigger);
    next.searchParams.append('sections', sectionId);
    next.hash = '';

    return next.toString();
  }

  #pickQuickViewSectionHtml(data, sectionId) {
    if (!data || typeof data !== 'object') return '';

    const isPdpUpsellSection = sectionId === 'quick-view-pdp-upsell';
    const upsellMarker = 'data-quick-view-pdp-upsell';

    const preferred = data[sectionId];

    if (typeof preferred === 'string' && preferred.length > 0) {
      if (!isPdpUpsellSection || preferred.includes(upsellMarker)) {
        return preferred;
      }
    }

    for (const [key, val] of Object.entries(data)) {
      if (val == null || typeof val !== 'string' || val.length === 0) continue;

      if (!val.includes('quick-view__container')) continue;

      if (isPdpUpsellSection) {
        if (key.includes(sectionId) && val.includes(upsellMarker)) {
          return val;
        }

        continue;
      }

      if (key.includes('quick-view-pdp-upsell')) {
        continue;
      }

      if (key.includes(sectionId) || key === 'quick-view') {
        return val;
      }
    }

    return '';
  }

  #showLoadingState(contentContainer) {
    const loadingTemplate = document.getElementById('QuickViewLoadingTemplate');

    if (loadingTemplate?.content) {
      contentContainer.replaceChildren(loadingTemplate.content.cloneNode(true));
      return;
    }

    contentContainer.replaceChildren();
  }

  async #fetchSectionRenderingJson(url) {
    let response = await fetch(url);

    if (response.status === 503) {
      await new Promise((r) => setTimeout(r, 400));
      response = await fetch(url);
    }

    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async open(url, trigger) {
    const dock = document.getElementById('QuickViewDock');
    if (!dock) return;

    // 1. Open Dock
    if (window.dockManager) {
      window.dockManager.open(dock);
    } else {
      dock.classList.add('active');
      document.body.classList.add('dock-open');
    }

    const contentContainer = document.getElementById('QuickViewContent');
    if (!contentContainer) return;

    const isPdpUpsellQuickView = this.#isPdpUpsellQuickViewTrigger(trigger);
    const isBundleQuickView = this.#isBundleQuickViewTrigger(trigger) || this.#isBundleSizeGuideTrigger(trigger);

    if (isPdpUpsellQuickView) {
      contentContainer.dataset.pdpUpsellQuickView = 'true';
    } else {
      delete contentContainer.dataset.pdpUpsellQuickView;
    }

    // 2. Show Loading State
    this.#showLoadingState(contentContainer);

    // 3. Ensure dependencies (main-product.js) are loaded
    this.loadDependencies();

    const sectionId = this.#sectionIdForTrigger(trigger);

    try {
      // 4. Fetch Product Section (Section Rendering API). Prefer locale-prefixed URL; fall back to plain product URL.
      const primaryUrl = this.#productUrlForSectionRequest(url, sectionId, trigger);
      let data = await this.#fetchSectionRenderingJson(primaryUrl);
      let sectionHtml = this.#pickQuickViewSectionHtml(data, sectionId);

      if (!sectionHtml) {
        const fallback = new URL(url, window.location.origin);
        this.#appendVariantParam(fallback, trigger);
        fallback.searchParams.append('sections', sectionId);
        fallback.hash = '';
        data = await this.#fetchSectionRenderingJson(fallback.toString());
        sectionHtml = this.#pickQuickViewSectionHtml(data, sectionId);
      }

      // 5. Inject Content
      if (sectionHtml) {
        contentContainer.innerHTML = sectionHtml;

        requestAnimationFrame(() => {
          contentContainer.dispatchEvent(new CustomEvent('quick-view:content', { bubbles: true }));
          contentContainer.dispatchEvent(new CustomEvent('quick-view:loaded', { bubbles: true }));
        });

        if (isBundleQuickView) {
          this.#initBundleSizeGuides(contentContainer);
        }

      } else {
        console.error('Quick View: no section HTML. Keys received:', data ? Object.keys(data) : data);
        const errP = document.createElement('p');
        errP.className = 'error';
        errP.textContent = `Could not load product. Section '${sectionId}' not found in response.`;
        contentContainer.replaceChildren(errP);
      }
    } catch (error) {
      console.error('Quick View Error:', error);
      const errP = document.createElement('p');
      errP.className = 'error';
      const detail = error instanceof Error ? error.message : String(error);
      errP.textContent = `Error loading product: ${detail}`;
      contentContainer.replaceChildren(errP);
    }
  }

  loadDependencies() {
    const dock = document.getElementById('QuickViewDock');
    if (!dock) return;

    const scripts = [
      dock.dataset.productScript,
      dock.dataset.carouselScript,
      dock.dataset.formScript,
      dock.dataset.productDescriptionScript,
    ];

    for (const scriptUrl of scripts) {
      if (scriptUrl && !document.querySelector(`script[src="${scriptUrl}"]`)) {
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.defer = true;
        document.head.appendChild(script);
      }
    }
  }

  #loadScriptOnce(src) {
    return new Promise((resolve) => {
      if (!src) {
        resolve();
        return;
      }

      const existing = document.querySelector(`script[src="${src}"]`);

      if (existing) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', () => resolve(), { once: true });
      document.head.appendChild(script);
    });
  }

  async #initBundleSizeGuides(container) {
    const dock = document.getElementById('QuickViewDock');

    if (!dock) return;

    await Promise.all([
      this.#loadScriptOnce(dock.dataset.sizeChartScript),
      this.#loadScriptOnce(dock.dataset.sizeQuizScript),
    ]);

    if (typeof window.initSizeChartElement === 'function') {
      for (const el of container.querySelectorAll('.size-chart-content')) {
        window.initSizeChartElement(el);
      }
    }

    if (typeof window.initSizeQuizElement === 'function') {
      for (const el of container.querySelectorAll('.js-size-quiz')) {
        window.initSizeQuizElement(el);
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.quickView = new QuickView();
});
